import React, { useState } from "react";
import Modal from "../../../components/modal";
import { API_URL } from "../../../utils/constant";
import axios from "axios";
import Swal from "sweetalert2";

const StockOtherScrapModal = ({
  show,
  onClose,
  heading = "Stock Details",
  initialData = {},
  billType = "Retail",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [localVariations, setLocalVariations] = useState(null);

  const baseVariations = (initialData?.variations && initialData.variations.length > 0)
    ? initialData.variations
    : (initialData?.id || initialData?.name ? [initialData] : []);

  const variations = localVariations || baseVariations;

  // Reset local state when initialData changes
  React.useEffect(() => {
    setLocalVariations(null);
  }, [initialData]);

  const handleTransfer = async (item, direction) => {
    const fromLabel = direction === 'wholesale_to_retail' ? 'Wholesale' : 'Retail';
    const toLabel = direction === 'wholesale_to_retail' ? 'Retail' : 'Wholesale';
    const maxQty = direction === 'wholesale_to_retail'
      ? (parseFloat(item.current_wholesale) || 0)
      : (parseFloat(item.qty) || 0);

    const { value: qty } = await Swal.fire({
      title: `Transfer ${fromLabel} → ${toLabel}`,
      html: `<p><b>${item.variant_name || 'Variation'}</b></p>
             <p>Retail Qty: <b>${item.qty || 0}</b> | Wholesale Qty: <b>${item.current_wholesale || 0}</b></p>
             <p>Max transferable: <b>${maxQty}</b></p>`,
      input: 'number',
      inputLabel: 'Quantity to transfer',
      inputAttributes: { min: 1, max: maxQty, step: 1 },
      inputValidator: (value) => {
        if (!value || parseFloat(value) <= 0) return 'Enter a valid quantity';
        if (parseFloat(value) > maxQty) return `Cannot exceed ${maxQty}`;
      },
      showCancelButton: true,
      confirmButtonText: 'Transfer',
      confirmButtonColor: '#198754'
    });

    if (!qty) return;

    try {
      const variationId = item.id_variation || item.id;
      await axios.put(`${API_URL}/final-products/transfer-stock/${variationId}`, {
        qty_to_transfer: qty,
        direction
      });

      const transferAmt = parseFloat(qty);
      setLocalVariations(prev => {
        const source = prev || baseVariations;
        return source.map(v => {
          const vId = v.id_variation || v.id;
          const itemId = item.id_variation || item.id;
          if (vId === itemId) {
            return {
              ...v,
              qty: direction === 'wholesale_to_retail'
                ? (parseFloat(v.qty) || 0) + transferAmt
                : (parseFloat(v.qty) || 0) - transferAmt,
              current_wholesale: direction === 'wholesale_to_retail'
                ? (parseFloat(v.current_wholesale) || 0) - transferAmt
                : (parseFloat(v.current_wholesale) || 0) + transferAmt
            };
          }
          return v;
        });
      });

      Swal.fire({
        icon: 'success',
        title: 'Transferred!',
        text: `${qty} units moved from ${fromLabel} to ${toLabel}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500
      });
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Transfer failed', 'error');
    }
  };

  const getItemYear = (item) => {
    if (item.year) return String(item.year);
    if (item.price_date) {
      const d = new Date(item.price_date);
      if (!isNaN(d.getFullYear())) return String(d.getFullYear());
    }
    if (item.expiry_date) {
      const d = new Date(item.expiry_date);
      if (!isNaN(d.getFullYear())) return String(d.getFullYear());
    }
    if (item.manufacturing_date) {
      const d = new Date(item.manufacturing_date);
      if (!isNaN(d.getFullYear())) return String(d.getFullYear());
    }
    if (item.createdAt) {
      const d = new Date(item.createdAt);
      if (!isNaN(d.getFullYear())) return String(d.getFullYear());
    }
    return 'N/A';
  };

  const filtered = variations.filter(v => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (v.variant_name && v.variant_name.toLowerCase().includes(term)) ||
      (v.batch_no && v.batch_no.toLowerCase().includes(term)) ||
      (v.exp_date && String(v.exp_date).toLowerCase().includes(term)) ||
      (v.serial_no && v.serial_no.toLowerCase().includes(term)) ||
      (getItemYear(v).includes(term))
    );
  });

  // Calculate Summary Totals
  const totalRetailStock = variations.reduce((sum, v) => sum + (parseFloat(v.qty || v.quantity) || 0), 0);
  const totalWholesaleStock = variations.reduce((sum, v) => sum + (parseFloat(v.current_wholesale) || 0), 0);
  const totalLooseStock = variations.reduce((sum, v) => sum + (parseFloat(v.loose_qty) || 0), 0);

  return (
    <Modal show={show} onClose={onClose} heading={heading || 'Stock Details'} size="lg">
      <div className="p-2">

        {/* Product Info Header */}
        <div className="d-flex align-items-center gap-3 p-3 bg-light rounded mb-3 border">
          <img
            src={`${API_URL}/${initialData?.product_image}`}
            alt={initialData?.name || 'Product'}
            className="rounded border p-1 bg-white"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/logo.png';
            }}
            style={{ width: 60, height: 60, objectFit: 'contain' }}
          />
          <div className="flex-grow-1">
            <h5 className="mb-1 font-bold text-dark">{initialData?.name}</h5>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {initialData?.brand_name && (
                <span className="badge bg-primary">Brand: {initialData.brand_name}</span>
              )}
              {initialData?.category_name && (
                <span className="badge bg-secondary">Category: {initialData.category_name}</span>
              )}
              <span className="badge bg-info text-white">Total Retail Stock: {totalRetailStock}</span>
              <span className="badge bg-success text-white">Total Wholesale Stock: {totalWholesaleStock}</span>
              {totalLooseStock > 0 && (
                <span className="badge bg-dark text-white">Total Loose Stock: {totalLooseStock}</span>
              )}
            </div>
          </div>
        </div>

        {/* Filter Input */}
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search potency/size, batch no, year, exp date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Variations Stock Table */}
        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table className="table table-bordered table-hover align-middle text-center mb-0">
            <thead className="table-dark sticky-top">
              <tr>
                <th>#</th>
                <th>Potency / Size</th>
                <th>Year</th>
                <th>Batch No</th>
                <th>Expiry Date</th>
                <th>Retail Stock</th>
                <th>Wholesale Stock</th>
                {totalLooseStock > 0 && <th>Loose Stock</th>}
                <th>Retail Price (₹)</th>
                <th>Wholesale Price (₹)</th>
                <th>Price Date</th>
                <th>Transfer</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={totalLooseStock > 0 ? 12 : 11} className="text-muted py-4">
                    No stock records found matching your search.
                  </td>
                </tr>
              ) : (
                filtered.map((item, idx) => {
                  const rPrice = parseFloat(item.price || item.sale_cost || 0).toFixed(2);
                  const wPrice = parseFloat(item.wholesale_price || item.price || item.sale_cost || 0).toFixed(2);
                  const yearVal = getItemYear(item);

                  return (
                    <tr key={idx} className={item.qty > 0 || item.current_wholesale > 0 ? '' : 'table-light text-muted'}>
                      <td>{idx + 1}</td>
                      <td className="fw-bold text-dark text-start">
                        {item.variant_name || initialData?.name}
                        {item.serial_no ? <small className="text-muted d-block">({item.serial_no})</small> : ''}
                      </td>
                      <td>
                        <span className="badge bg-dark">{yearVal}</span>
                      </td>
                      <td>
                        {item.batch_no ? (
                          <span className="badge bg-warning text-dark border border-warning">
                            {item.batch_no}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {item.exp_date ? (
                          <span className="badge bg-light text-danger border border-danger">
                            {new Date(item.exp_date).toLocaleDateString('en-IN')}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        <span className={`badge ${parseFloat(item.qty) > 0 ? 'bg-primary' : 'bg-secondary'}`}>
                          {item.qty || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${parseFloat(item.current_wholesale) > 0 ? 'bg-success' : 'bg-secondary'}`}>
                          {item.current_wholesale || 0}
                        </span>
                      </td>
                      {totalLooseStock > 0 && (
                        <td>
                          <span className="badge bg-info text-white">
                            {item.loose_qty || 0}
                          </span>
                        </td>
                      )}
                      <td className="fw-bold text-primary">₹{rPrice}</td>
                      <td className="fw-bold text-success">₹{wPrice}</td>
                      <td>
                        {item.price_date ? new Date(item.price_date).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          {parseFloat(item.current_wholesale) > 0 && (
                            <button
                              className="btn btn-outline-success btn-sm py-0 px-1"
                              style={{ fontSize: '0.7rem' }}
                              onClick={(e) => { e.stopPropagation(); handleTransfer(item, 'wholesale_to_retail'); }}
                              title="Transfer Wholesale → Retail"
                            >
                              W→R
                            </button>
                          )}
                          {parseFloat(item.qty) > 0 && (
                            <button
                              className="btn btn-outline-warning btn-sm py-0 px-1"
                              style={{ fontSize: '0.7rem' }}
                              onClick={(e) => { e.stopPropagation(); handleTransfer(item, 'retail_to_wholesale'); }}
                              title="Transfer Retail → Wholesale"
                            >
                              R→W
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot className="table-light fw-bold">
              <tr>
                <td colSpan="5" className="text-end">Total Summary:</td>
                <td>
                  <span className="badge bg-primary fs-14">{totalRetailStock}</span>
                </td>
                <td>
                  <span className="badge bg-success fs-14">{totalWholesaleStock}</span>
                </td>
                {totalLooseStock > 0 && (
                  <td>
                    <span className="badge bg-info text-white fs-14">{totalLooseStock}</span>
                  </td>
                )}
                <td colSpan="4"></td>
              </tr>
            </tfoot>
          </table>
        </div>

      </div>
    </Modal>
  );
};

export default StockOtherScrapModal;
